/**
 * Date and timezone utility functions for the PEP application
 * Handles conversions between different timezones, particularly clinic timezones and IST
 */

/**
 * Formats a date string to IST (Asia/Kolkata) timezone
 * @param dateString - ISO date string or date string
 * @param includeDate - Whether to include the date part (default: true)
 * @param includeTime - Whether to include the time part (default: true)
 * @returns Formatted date/time string in IST
 */
export const formatToIST = (
  dateString: string, 
  includeDate: boolean = true, 
  includeTime: boolean = true
): string => {
  if (!dateString) return 'Not Set';
  
  try {
    const date = new Date(dateString);
    
    if (includeDate && includeTime) {
      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } else if (includeDate) {
      return date.toLocaleDateString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } else if (includeTime) {
      return date.toLocaleTimeString('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
    
    return 'Invalid format options';
  } catch (error) {
    console.error('Error formatting date to IST:', error);
    return 'Invalid Date';
  }
};

/**
 * Formats a date string to a specific timezone
 * @param dateString - ISO date string or date string
 * @param timezone - Target timezone (e.g., 'US/Pacific', 'US/Eastern', 'Europe/London')
 * @param includeDate - Whether to include the date part (default: true)
 * @param includeTime - Whether to include the time part (default: true)
 * @returns Formatted date/time string in the specified timezone
 */
export const formatToTimezone = (
  dateString: string, 
  timezone: string,
  includeDate: boolean = true, 
  includeTime: boolean = true
): string => {
  if (!dateString) return 'Not Set';
  if (!timezone) return formatToIST(dateString, includeDate, includeTime);
  
  try {
    const date = new Date(dateString);
    
    if (includeDate && includeTime) {
      return date.toLocaleString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } else if (includeDate) {
      return date.toLocaleDateString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } else if (includeTime) {
      return date.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
    
    return 'Invalid format options';
  } catch (error) {
    console.error(`Error formatting date to timezone ${timezone}:`, error);
    // Fallback to IST if timezone conversion fails
    return formatToIST(dateString, includeDate, includeTime);
  }
};

/**
 * Formats appointment time for clinic timezone display
 * Used for "Appointment in Clinic Time" column
 * @param dateString - Appointment date/time string
 * @param clinicTimezone - Patient's clinic timezone (e.g., 'US/Pacific')
 * @returns Formatted appointment string in clinic timezone
 */
export const formatAppointmentClinicTime = (
  dateString: string, 
  clinicTimezone: string
): string => {
  if (!dateString) return 'Appointment Not Set';
  
  return formatToTimezone(dateString, clinicTimezone, true, true);
};

/**
 * Formats appointment time for IST display
 * Used for "Appointment in Local Time" column
 * @param dateString - Appointment date/time string
 * @returns Formatted appointment string in IST
 */
export const formatAppointmentISTTime = (dateString: string): string => {
  if (!dateString) return 'Appointment Not Set';
  
  return formatToIST(dateString, true, true);
};

/**
 * Formats appointment time for browser's local timezone
 * Used for "Appointment in Local Time" column
 * @param dateString - Appointment date/time string
 * @returns Formatted appointment string in user's browser timezone
 */
export const formatAppointmentLocalTime = (dateString: string): string => {
  if (!dateString) return 'Appointment Not Set';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting appointment local time:', error);
    return 'Invalid Date';
  }
};

/**
 * Legacy date formatter for backward compatibility
 * Formats date only (no time) in local format
 * @param dateString - Date string
 * @returns Formatted date string
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return 'Not Set';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Legacy time formatter for backward compatibility
 * Formats time only (no date) in local format
 * @param dateString - Date/time string
 * @returns Formatted time string
 */
export const formatDateTime = (dateString: string): string => {
  if (!dateString) return 'Not Set';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid Time';
  }
};

/**
 * Gets timezone abbreviation for display
 * @param timezone - Full timezone name (e.g., 'US/Pacific')
 * @returns Timezone abbreviation (e.g., 'PST' or 'PDT')
 */
export const getTimezoneAbbreviation = (timezone: string): string => {
  if (!timezone) return 'IST';
  
  try {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    
    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    return timeZonePart?.value || timezone;
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error);
    return timezone;
  }
};

/**
 * Validates if a timezone string is valid
 * @param timezone - Timezone string to validate
 * @returns Boolean indicating if timezone is valid
 */
export const isValidTimezone = (timezone: string): boolean => {
  if (!timezone) return false;
  
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch (error) {
    return false;
  }
};

export const setAndformatDateLocally = (dateString: string): string => {
  if (!dateString) return 'Not Set';
  try {
    const day = dateString.split('-')[2];
    const month = dateString.split('-')[1];
    const year = dateString.split('-')[0];
    const date = new Date();
    if (day && month && year && !isNaN(Number(day)) && !isNaN(Number(month)) && !isNaN(Number(year))) {
      date.setFullYear(Number(year));
      date.setMonth((Number(month) - 1));
      date.setDate(Number(day));
      date.setHours(0);
      date.setMinutes(0);
    } else {
      return 'Invalid Date';
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid Time';
  }
};

export const getLocalDate = (dateString: string): Date => {
  if (!dateString) return null;
  try {
    const day = dateString.split('-')[2];
    const month = dateString.split('-')[1];
    const year = dateString.split('-')[0];
    const date = new Date();
    if (day && month && year && !isNaN(Number(day)) && !isNaN(Number(month)) && !isNaN(Number(year))) {
      date.setFullYear(Number(year));
      date.setMonth((Number(month) - 1));
      date.setDate(Number(day));
      date.setHours(0);
      date.setMinutes(0);
    } else {
      return null;
    }
    return date;
  } catch (error) {
    console.error('Error formatting time:', error);
    return null;
  }
};
